/* eslint-disable unicorn/filename-case -- TODO rename later */
import { cn } from "@istari-frontend/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { InfosecLevel, V3Models } from "@istari/istari-client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { FileWithPath } from "react-dropzone";

import { useGetControlTags } from "~/api/useControlTags";
import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";
import getFileSVGIconByExtension from "~/components/file-svg-icon";
import { NAME_INPUT_MAX_LENGTH } from "~/constants";
import { InfoSecTagsMenu } from "~/control-tags/ui/infosec-tags-menu";
import { useAllowedInfosecLevels } from "~/hooks";
import { useUploadResource } from "~/resource/data-source/use-upload-resource";
import { getFileNameAndExtension } from "~/utils/file";
import { generateUniqueId } from "~/utils/helper";
import { formatInfosecLevelName } from "~/utils/infosec";

type FileItemType = {
  displayName: string;
  extension: string;
  externalIdentifier: string;
  file: File;
  /** Slash-separated folder path the file came from, rooted at the dropped folder, or null for a loose file. */
  originPath: string | null;
  id: string;
  tags: string[];
  resourceType: V3Models.ResourceCreateTypeDto;
  infosecLevelId?: string | null;
};

interface UploadSpecModalProps {
  files: FileWithPath[] | undefined;
  open: boolean;
  setModalOpen: (value: boolean) => void;
  description?: string;
  systemInfosecLevel?: InfosecLevel | null;
  onFilesUploaded?: (
    filesWithMetadata: Array<{
      displayName: string;
      extension: string;
      externalIdentifier: string;
      file: File;
      originPath: string | null;
      tags: string[];
      resourceType: V3Models.ResourceCreateTypeDto;
      infosecLevelId?: string | null;
    }>,
  ) => void;
}

const UploadSpecModal = ({
  files,
  open,
  setModalOpen,
  description = "Uploaded resources will be added to the workspace.",
  systemInfosecLevel,
  onFilesUploaded,
}: UploadSpecModalProps) => {
  return (
    <Dialog open={open} onOpenChange={setModalOpen}>
      {open && (
        <UploadSpecModalContent
          files={files}
          setModalOpen={setModalOpen}
          description={description}
          systemInfosecLevel={systemInfosecLevel}
          onFilesUploaded={onFilesUploaded}
        />
      )}
    </Dialog>
  );
};

type UploadSpecModalContentProps = Omit<UploadSpecModalProps, "open">;

function UploadSpecModalContent({
  files,
  setModalOpen,
  description,
  systemInfosecLevel,
  onFilesUploaded,
}: UploadSpecModalContentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const formId = useId();
  const { data: controlTags } = useGetControlTags();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const allowedInfosecLevels = useAllowedInfosecLevels(systemInfosecLevel);

  const isInfosecEnabled = platformSettingsQuery.data?.enabled ?? false;
  const defaultLevelId = platformSettingsQuery.data?.defaultResourceLevel?.id;

  const { uploadFiles } = useUploadResource();
  const [fileItems, setFileItems] = useState<Record<string, FileItemType>>({});

  const hasOneEmptyFilename = Object.values(fileItems).some((f) => !f.displayName.length);
  const hasOneLongExternalVersion = Object.values(fileItems).some(
    (f) => f.externalIdentifier.length > NAME_INPUT_MAX_LENGTH,
  );
  const hasEmptyInfosecLevel = isInfosecEnabled && Object.values(fileItems).some((f) => !f.infosecLevelId);

  useEffect(() => {
    if (files) {
      setFileItems(toFileItems(files, defaultLevelId || null));
    }
  }, [files, defaultLevelId]);

  const appendMoreFiles = useCallback(
    (moreFiles: FileList | null) => {
      if (!moreFiles || moreFiles.length === 0) return;
      const addendum = toFileItems(Array.from(moreFiles), defaultLevelId || null);
      setFileItems((prev) => ({ ...prev, ...addendum }));
    },
    [defaultLevelId],
  );

  const handleEntryChange = ({ id, key, value }: { id: string; key: string; value: string | string[] | null }) => {
    setFileItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: value },
    }));
  };

  const handleAddMoreFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileUpload = useCallback(() => {
    if (onFilesUploaded) {
      onFilesUploaded(Object.values(fileItems));
    } else {
      uploadFiles(Object.values(fileItems));
    }
    setModalOpen(false);
  }, [fileItems, setModalOpen, uploadFiles, onFilesUploaded]);

  return (
    // `@container` makes DialogContent the query container for the `@2xl:*` layout switch below.
    // Its width is 80vw of whichever window it renders in: the dialog portals through the app's
    // portal-container context (dialog.tsx), so when opened from a popped-out chat it renders in that
    // (narrow) window — 80vw there is narrow, so `@2xl` (672px) doesn't hit and the stacked layout
    // applies; in the main window 80vw is wide and the horizontal grid applies. That is what makes it
    // responsive to the chat window rather than always the main viewport.
    <DialogContent
      ref={dialogContentRef}
      className="@container flex !min-w-[80vw] flex-col"
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Upload Resources</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          onFileUpload();
        }}
      >
        <div className="min-h-0 flex-1">
          <div
            className={cn(
              "bg-muted/50 mb-2 hidden gap-2.5 truncate rounded border px-3 py-2 text-sm @2xl:grid",
              isInfosecEnabled ? "@2xl:grid-cols-[2fr_1.5fr_200px_200px]" : "@2xl:grid-cols-[2fr_1.5fr_300px]",
            )}
          >
            <div className="flex items-center gap-1 font-medium">
              <div className="w-6"></div>
              Resource name <span className="text-destructive">*</span>
            </div>
            <div className="flex items-center font-medium">External Identifier</div>
            <div className="flex items-center font-medium">Tags</div>
            {isInfosecEnabled && (
              <div className="flex items-center font-medium">
                Infosec Level <span className="text-destructive">*</span>
              </div>
            )}
          </div>

          <div className="max-h-[90vh] space-y-2.5 overflow-y-auto py-1">
            {Object.values(fileItems).map((f) => (
              <div
                className={cn(
                  "grid grid-cols-[minmax(6rem,auto)_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-lg border p-3",
                  "@2xl:gap-2.5 @2xl:rounded-none @2xl:border-0 @2xl:p-0",
                  isInfosecEnabled ? "@2xl:grid-cols-[2fr_1.5fr_200px_200px]" : "@2xl:grid-cols-[2fr_1.5fr_300px]",
                )}
                key={f.id}
              >
                <span className="text-sm font-medium @2xl:hidden">
                  Resource name <span className="text-destructive">*</span>
                </span>
                <div className="flex items-center gap-1">
                  <div className="flex-shrink-0">{getFileSVGIconByExtension(fileItems[f.id].extension)}</div>
                  <Input
                    required
                    aria-label="Resource Name"
                    className={cn(
                      "min-w-0 flex-1",
                      !fileItems[f.id].displayName.length &&
                        "border-destructive ring-destructive/20 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
                    )}
                    value={fileItems[f.id].displayName}
                    onChange={(e) => handleEntryChange({ id: f.id, key: "displayName", value: e.target.value })}
                    aria-invalid={!fileItems[f.id].displayName.length}
                  />
                </div>
                <span className="text-sm font-medium @2xl:hidden">External Identifier</span>
                <Input
                  aria-label="External Identifier"
                  className={cn(
                    "min-w-0",
                    fileItems[f.id].externalIdentifier.length > NAME_INPUT_MAX_LENGTH &&
                      "border-destructive ring-destructive/20 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
                  )}
                  value={fileItems[f.id].externalIdentifier}
                  onChange={(e) => handleEntryChange({ id: f.id, key: "externalIdentifier", value: e.target.value })}
                  aria-invalid={fileItems[f.id].externalIdentifier.length > NAME_INPUT_MAX_LENGTH}
                />
                <span className="text-sm font-medium @2xl:hidden">Tags</span>
                <InfoSecTagsMenu
                  options={controlTags}
                  onSelectOptions={(tags) =>
                    handleEntryChange({ id: f.id, key: "tags", value: tags.map((tag) => tag.value) })
                  }
                  container={dialogContentRef.current}
                />
                {isInfosecEnabled && (
                  <>
                    <span className="text-sm font-medium @2xl:hidden">
                      Infosec Level <span className="text-destructive">*</span>
                    </span>
                    <div className="flex items-center">
                      <Select
                        required
                        value={fileItems[f.id].infosecLevelId ?? ""}
                        onValueChange={(value) =>
                          handleEntryChange({ id: f.id, key: "infosecLevelId", value: value || null })
                        }
                      >
                        <SelectTrigger
                          className={cn(
                            "w-full",
                            !fileItems[f.id].infosecLevelId &&
                              "border-destructive ring-destructive/20 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
                          )}
                          aria-invalid={!fileItems[f.id].infosecLevelId}
                          data-testid={`upload-infosec-level-select-${f.id}`}
                        >
                          <SelectValue placeholder="Select level..." />
                        </SelectTrigger>
                        <SelectContent data-testid={`upload-infosec-level-content-${f.id}`}>
                          {allowedInfosecLevels.map((level) => (
                            <SelectItem
                              key={level.id}
                              value={level.id}
                              data-testid={`upload-infosec-option-${level.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: level.displayColor }} />
                                {formatInfosecLevelName(level.name)}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </form>
      <DialogFooter>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => appendMoreFiles(e.target.files)}
        />
        <Button variant="ghost" onClick={() => setModalOpen(false)} data-testid="modal-cancel-button">
          Cancel
        </Button>
        <Button variant="outline" type="button" onClick={handleAddMoreFiles} data-testid="add-more-files-button">
          Add more files
        </Button>
        <Button
          data-testid="upload-files-spec-upload-button"
          disabled={hasOneEmptyFilename || hasOneLongExternalVersion || hasEmptyInfosecLevel}
          type="submit"
          form={formId}
        >
          Upload
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function toFileItems(files: readonly FileWithPath[], infosecLevelId: string | null): Record<string, FileItemType> {
  return Object.fromEntries(
    files.map((file) => {
      const id = generateUniqueId();
      const { name, extension } = getFileNameAndExtension(file.name) ?? {};
      return [
        id,
        {
          displayName: name || "",
          extension: extension || "",
          externalIdentifier: "",
          file,
          originPath: folderPathOf(file),
          id,
          tags: [],
          resourceType: V3Models.ResourceCreateTypeDto.Model,
          infosecLevelId,
        },
      ];
    }),
  );
}

// `react-dropzone`'s `onDrop` always sets `file.path` ("/docs/spec.txt" or just "spec.txt" for a
// loose file with no directory separator, hence the `null` below). `webkitRelativePath` is a
// forward-looking fallback for a `webkitdirectory`-style picker, which this app doesn't have yet.
function folderPathOf(file: FileWithPath): string | null {
  const originPath = (file.path || file.webkitRelativePath || "").replace(/^\.?\//, "");
  const lastSeparator = originPath.lastIndexOf("/");
  return lastSeparator === -1 ? null : originPath.slice(0, lastSeparator);
}

export default UploadSpecModal;
